import axios from 'axios';
import jwtDecode from 'jwt-decode';
import store from '../Redux/store';

import { saveAccessToken, deleteTokens } from '../Redux/Tokens';

// In minutes
export const AUTO_REFRESH_THRESHOLD = 2;
export const MANUAL_REFRESH_THRESHOLD = 2;

interface JWT {
  iat: number;
  nbf: number;
  jti: string;
  exp: number;
  identity: string;
  fresh: boolean;
  type: string;
}

/**
 * This function will call the backend to refresh the access token
 */
export function refreshAccessToken(): void {
  const { refreshToken } = store.getState().tokens;

  axios
    .get('/api/refresh', {
      headers: { Authorization: `Bearer ${refreshToken}` },
    })
    .then((res) => {
      if (res.status === 200) {
        const { access_token } = res.data;
        store.dispatch(saveAccessToken(access_token));
      }
    })
    .catch(() => {
      // If there is any errors with the tokens, just delete it
      store.dispatch(deleteTokens());
    });
}

/*
    This function is used to validate the access token.
    If the token is near expiry, it will use the refresh token to update it.
    If it has expired, it will remove all the tokens.
   */
export function checkAccessToken(): void {
  const { accessToken } = store.getState().tokens;
  if (!accessToken) {
    return;
  }

  const decoded = jwtDecode(accessToken || '') as JWT;

  const expireDate = new Date(decoded.exp * 1000);
  const currentTime = new Date(Date.now());
  const refreshThreshold = new Date(currentTime);
  const deleteThreshold = new Date(currentTime);

  refreshThreshold.setMinutes(
    refreshThreshold.getMinutes() + AUTO_REFRESH_THRESHOLD,
  );

  deleteThreshold.setMinutes(
    deleteThreshold.getMinutes() - MANUAL_REFRESH_THRESHOLD,
  );

  if (deleteThreshold >= expireDate) {
    deleteTokens();
  }

  if (currentTime >= expireDate) {
    // Token has expired. We are not going to auto refresh
    return;
  }

  if (refreshThreshold >= expireDate) {
    // We are nearly about to expire!
    refreshAccessToken();
  }
}

export function getAccessExpiry(): number {
  const { accessToken } = store.getState().tokens;

  if (!accessToken) {
    return 0;
  }

  const decoded = jwtDecode(accessToken || '') as JWT;

  const expireDate = new Date(decoded.exp * 1000);
  const now = new Date(Date.now());

  return expireDate.valueOf() - now.valueOf();
}

export function getRefreshExpiry(): number {
  const { refreshToken } = store.getState().tokens;

  if (!refreshToken) {
    return 0;
  }

  const decoded = jwtDecode(refreshToken || '') as JWT;

  const expireDate = new Date(decoded.exp * 1000);
  const now = new Date(Date.now());

  return expireDate.valueOf() - now.valueOf();
}
